import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Alert, Container, CircularProgress, Box, Typography,
} from '@mui/material';
import ReportsList from './ReportsList';
import KeycloakContext from '../../KeycloakProvider';
import { canAccessSidebarKey } from '../../utils/accessUtil';
import { GROUP_MATRIX } from '../../utils/accessRegistry';
import { reportError } from '../../utils/reportError';
import { baseUrl } from '../../api/baseQuery';
import { getAuthHeaders } from '../../utils/authHeaders';
import ContentUnavailable from '../../components/ContentUnavailable';

const getPeriodFromFilename = (filename) => {
  const lower = filename.toLowerCase();
  if (lower.includes('daily')) return 'daily';
  if (lower.includes('weekly')) return 'weekly';
  if (lower.includes('monthly')) return 'monthly';
  return null;
};

/**
 * SiteReports – generic KPI site page (SU, Caesars, FOA, Windsor).
 *
 * Props:
 *   folder     – backend folder name: 'su' | 'caesars' | 'foa' | 'windsor'
 *   title      – page heading (e.g. "Syracuse University")
 *   sidebarKey – key used in GROUP_MATRIX for access control (e.g. 'kpi_su')
 */
const SiteReports = ({ folder, title, sidebarKey }) => {
  const { keycloak } = useContext(KeycloakContext);
  const userGroups = keycloak?.tokenParsed?.realm_access?.roles || [];
  const hasAccess = canAccessSidebarKey(userGroups, sidebarKey, GROUP_MATRIX);

  // Determine if user is T-Mobile, Pierson, or AT&T
  const userCustomerGroup = 
    userGroups.includes('T-Mobile') ? 'T-Mobile' :
    userGroups.includes('Pierson') ? 'Pierson' :
    userGroups.includes('AT&T') ? 'AT&T' :
    null;
  
  // Determine if user is internal (can see all reports)
  const internalGroups = ['admin', 'NOC', 'Manager', 'VP', 'Executive', 'Construction', 'Dashboard', 'C-Suite'];
  const isInternalUser = userGroups.some(role => internalGroups.includes(role));

  // Function to filter files by customer name in filename
  const filterByCustomerName = (files) => {
    // If user is internal or not from T-Mobile/Pierson/AT&T, show all files
    if (isInternalUser || !userCustomerGroup) {
      return files;
    }
    
    // Filter to only show files with customer name in filename (case-insensitive)
    return files.filter(file => {
      const filename = file.filename.toLowerCase();
      const customerName = userCustomerGroup.toLowerCase();
      return filename.includes(customerName);
    });
  };

  const [allFiles, setAllFiles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hasAccess) { setLoading(false); return; }

    const fetchFiles = async () => {
      try {
        setLoading(true);
        const headers = await getAuthHeaders({ Accept: 'application/json' });
        const response = await fetch(`${baseUrl}/listPdfFolder/?folder=${encodeURIComponent(folder)}`, {
          headers
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        const files = data.files || [];
        files.sort((a, b) => b.filename.localeCompare(a.filename));
        setAllFiles(files);
        if (files.length === 0) {
          reportError({ page: title, detail: `listPdfFolder/?folder=${folder} returned empty files array` });
        }
        setError(null);
      } catch (err) {
        setError('Failed to load reports: ' + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, [folder, hasAccess, title]);

  const filteredFiles = useMemo(() => {
    const allFilesArray = allFiles || [];
    return filterByCustomerName(allFilesArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- filterByCustomerName is recreated each render; its real inputs (userCustomerGroup, isInternalUser) are already listed
  }, [allFiles, userCustomerGroup, isInternalUser]);

  const dailyFiles = useMemo(() => filteredFiles.filter(f => getPeriodFromFilename(f.filename) === 'daily'), [filteredFiles]);
  const weeklyFiles = useMemo(() => filteredFiles.filter(f => getPeriodFromFilename(f.filename) === 'weekly'), [filteredFiles]);
  const monthlyFiles = useMemo(() => filteredFiles.filter(f => getPeriodFromFilename(f.filename) === 'monthly'), [filteredFiles]);

  if (!hasAccess) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          You do not have permission to access {title}. Please contact your administrator.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Container>;
  }

  if (error) {
    return <Container maxWidth="lg" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }

  // If customer user has no matching files after filtering, hide this page completely
  if (!loading && userCustomerGroup && filteredFiles.length === 0) {
    return null;
  }

  if (!loading && (!allFiles || allFiles.length === 0)) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ContentUnavailable page={title} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        {title}
      </Typography>

      {/* Daily section */}
      {dailyFiles.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <ReportsList
            endpoint="listPdfFolder/"
            title="Daily"
            allColumns={false}
            initialData={{ files: dailyFiles }}
            hideMainTitle={true}
            enableFilters={false}
            filenamePrefix={folder}
          />
        </Box>
      )}

      {/* Weekly section */}
      {weeklyFiles.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <ReportsList
            endpoint="listPdfFolder/"
            title="Weekly"
            allColumns={false}
            initialData={{ files: weeklyFiles }}
            hideMainTitle={true}
            enableFilters={false}
            filenamePrefix={folder}
          />
        </Box>
      )}

      {/* Monthly section */}
      {monthlyFiles.length > 0 && (
        <Box sx={{ mb: 5 }}>
          <ReportsList
            endpoint="listPdfFolder/"
            title="Monthly"
            allColumns={false}
            initialData={{ files: monthlyFiles }}
            hideMainTitle={true}
            enableFilters={false}
            filenamePrefix={folder}
          />
        </Box>
      )}

      {/* Files that don't match any period prefix */}
      {filteredFiles.filter(f => !getPeriodFromFilename(f.filename)).length > 0 && (
        <Box sx={{ mb: 5 }}>
          <ReportsList
            endpoint="listPdfFolder/"
            title="Quarterly"
            allColumns={false}
            initialData={{ files: filteredFiles.filter(f => !getPeriodFromFilename(f.filename)) }}
            hideMainTitle={true}
            enableFilters={false}
            filenamePrefix={folder}
          />
        </Box>
      )}
    </Container>
  );
};

export default SiteReports;
